import apiClient from '../config/api';

export interface AgreementDraft {
  draftId: number;
  agreementId: number | null; // Original agreement ID if editing
  hospitalUuid: string | null;
  validFrom: string | null;
  validTill: string | null;
  hospitalShareNlFormula: string | null;
  hospitalShareExecutable: string | null;
  arApNlFormula: string | null;
  arApExecutable: string | null;
  inputParameterIds: number[] | null;
  calculatedParameters: Array<{
    parameterName: string;
    naturalLanguageFormula: string;
    executableFormula?: string;
  }> | null;
  createdBy: string;
  createdByEmail?: string; // User email
  modifiedBy: string;
  createdAt: string;
  modifiedAt: string;
  hospitalName?: string;
}

export interface SaveDraftInput {
  draftId?: number;
  agreementId?: number; // Original agreement ID if editing
  hospitalUuid?: string;
  validFrom?: string;
  validTill?: string;
  hospitalShareNlFormula?: string;
  hospitalShareExecutable?: string;
  arApNlFormula?: string;
  arApExecutable?: string;
  inputParameterIds?: number[];
  calculatedParameters?: Array<{
    parameterName: string;
    naturalLanguageFormula: string;
    executableFormula?: string;
  }>;
}

function mapDraft(data: Record<string, unknown>): AgreementDraft {
  return {
    draftId: data.draft_id as number,
    agreementId: data.agreement_id as number | null,
    hospitalUuid: data.hospital_uuid as string | null,
    validFrom: data.valid_from as string | null,
    validTill: data.valid_till as string | null,
    hospitalShareNlFormula: data.hospital_share_nl_formula as string | null,
    hospitalShareExecutable: data.hospital_share_executable as string | null,
    arApNlFormula: data.ar_ap_nl_formula as string | null,
    arApExecutable: data.ar_ap_executable as string | null,
    inputParameterIds: data.input_parameter_ids as number[] | null,
    calculatedParameters: data.calculated_parameters as Array<{
      parameterName: string;
      naturalLanguageFormula: string;
      executableFormula?: string;
    }> | null,
    createdBy: data.created_by as string,
    createdByEmail: data.created_by_email as string | undefined,
    modifiedBy: data.modified_by as string,
    createdAt: data.created_at as string,
    modifiedAt: data.modified_at as string,
    hospitalName: data.hospital_name as string | undefined,
  };
}

export async function saveDraft(input: SaveDraftInput): Promise<AgreementDraft> {
  const response = await apiClient.post('/api/agreement-drafts', input);
  return mapDraft(response.data.draft);
}

export async function listDrafts(): Promise<AgreementDraft[]> {
  const response = await apiClient.get('/api/agreement-drafts');
  return (response.data.drafts || []).map(mapDraft);
}

export async function getDraft(draftId: number): Promise<AgreementDraft> {
  const response = await apiClient.get(`/api/agreement-drafts/${draftId}`);
  return mapDraft(response.data.draft);
}

export async function getDraftByAgreementId(agreementId: number): Promise<AgreementDraft | null> {
  try {
    const response = await apiClient.get(`/api/agreement-drafts/by-agreement/${agreementId}`);
    return response.data.draft ? mapDraft(response.data.draft) : null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function deleteDraft(draftId: number): Promise<void> {
  await apiClient.delete(`/api/agreement-drafts/${draftId}`);
}
